import { AlertCircle } from "lucide-react";

import type { ParsedResume } from "@/lib/cv/ats-check";
import { cn } from "@/lib/utils";

// Presentational only (no client hooks): renders in the server page (static
// sample, indexable) and the client tool (live parse). Mimics an ATS parser
// readout. Empty cells are flagged, an empty cell == not cleanly parsed.

function Row({
  label,
  value,
  descriptions,
}: {
  label: string;
  value?: string;
  descriptions?: string[];
}) {
  const hasValue = Boolean(value && value.trim());
  const hasDescriptions = Boolean(descriptions && descriptions.length > 0);
  const empty = !hasValue && !hasDescriptions;

  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 border-t border-border px-4 py-2.5 text-sm sm:grid-cols-[140px_1fr]">
      <div className="font-medium text-muted-foreground">{label}</div>
      <div>
        {hasValue ? <p className="text-foreground">{value}</p> : null}
        {hasDescriptions ? (
          <ul className="ml-4 list-disc space-y-0.5 text-muted-foreground">
            {descriptions!.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        ) : null}
        {empty ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
            <AlertCircle className="h-3.5 w-3.5" />
            Tidak terbaca
          </span>
        ) : null}
      </div>
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background">
      <div className="bg-muted px-4 py-2.5 text-sm font-semibold text-text-heading">
        {title}
      </div>
      {children}
    </div>
  );
}

export function AtsParsedTable({
  resume,
  title = "Begini CV kamu dibaca ATS",
  subtitle = "Parser memecah CV jadi field terstruktur. Kalau ada yang “Tidak terbaca”, ATS gagal menangkap info itu, dan recruiter bisa kehilangannya.",
  className,
}: {
  resume: ParsedResume;
  title?: string;
  subtitle?: string;
  className?: string;
}) {
  const { profile, educations, workExperiences, skills } = resume;

  return (
    <div className={cn("rounded-2xl border border-border bg-muted/30 p-4 sm:p-6", className)}>
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>

      <div className="mt-4 space-y-3">
        <Group title="Profil">
          <Row label="Nama" value={profile.name} />
          <Row label="Email" value={profile.email} />
          <Row label="Telepon" value={profile.phone} />
          <Row label="Lokasi" value={profile.location} />
          <Row label="Link" value={profile.link} />
          <Row label="Ringkasan" value={profile.summary} />
        </Group>

        {(educations.length > 0 ? educations : [null]).map((edu, index) => (
          <Group
            key={`edu-${index}`}
            title={educations.length > 1 ? `Pendidikan ${index + 1}` : "Pendidikan"}
          >
            <Row label="Institusi" value={edu?.school} />
            <Row label="Gelar/Jurusan" value={edu?.degree} />
            <Row label="IPK/GPA" value={edu?.gpa} />
            <Row label="Tanggal" value={edu?.date} />
            <Row label="Detail" descriptions={edu?.descriptions} />
          </Group>
        ))}

        {(workExperiences.length > 0 ? workExperiences : [null]).map((work, index) => (
          <Group
            key={`work-${index}`}
            title={
              workExperiences.length > 1
                ? `Pengalaman ${index + 1}`
                : "Pengalaman Kerja"
            }
          >
            <Row label="Perusahaan" value={work?.company} />
            <Row label="Jabatan" value={work?.jobTitle} />
            <Row label="Tanggal" value={work?.date} />
            <Row label="Detail" descriptions={work?.descriptions} />
          </Group>
        ))}

        <Group title="Keterampilan">
          <Row label="Skills" value={skills.join(", ")} />
        </Group>
      </div>
    </div>
  );
}
